/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { AgentAppCommandOutcome } from '@poweralpha/common';
import type { AppCommandAckResult } from '../session/contract';
import { getAgentClientId } from './clientId';

/** Identifies an app command in its conversation, for `app_command_ack`. */
export interface AppCommandRef {
  conversation_id: string;
  turn_id: string;
  app_command_id: string;
}

interface Ack extends AppCommandRef {
  type: 'app_command_ack';
  request_id: string;
  client_id: string;
  outcome: AgentAppCommandOutcome;
  reason: string | null;
}

/** Sends a socket message; returns false when it cannot right now (disconnected). */
type Sender = (payload: object) => boolean;

export const ACK_OUTBOX_STORAGE_KEY = 'pa-agent-app-command-acks';
const OUTBOX_KEY = ACK_OUTBOX_STORAGE_KEY;

// Kept in session storage (per tab, like the client id) so outcomes survive a reload until the
// server records them: commands are not applied again after a reload, so nothing would resend them.
const loadOutbox = () => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(OUTBOX_KEY) ?? '[]') as Array<[string, Ack]>;
    return new Map(Array.isArray(stored) ? stored : []);
  } catch {
    return new Map<string, Ack>();
  }
};

const saveOutbox = () => {
  try {
    sessionStorage.setItem(OUTBOX_KEY, JSON.stringify([...outbox]));
  } catch {
    // Without storage the outbox still lives until the tab reloads.
  }
};

// Per tab: one outbox for every agent surface (side panel, full screen), each a possible sender.
// Insertion order keeps a command's outcomes in order (e.g. pending before confirmed).
const outbox: Map<string, Ack> = loadOutbox();
const senders = new Set<Sender>();

/**
 * Drops every queued outcome (logout, account change): they belong to the previous account's
 * conversations and must not go out through the next account's session.
 */
export function clearAppCommandAcks() {
  outbox.clear();
  try {
    sessionStorage.removeItem(OUTBOX_KEY);
  } catch {
    // Storage unavailable: the in-memory queue is what matters here.
  }
}

const sendAll = () => {
  for (const ack of outbox.values()) {
    for (const send of senders) if (send(ack)) break;
  }
};

/**
 * Reports what this tab did with an app command (`app_command_ack`). Kept until the server
 * records it and sent again after reconnecting; the server ignores duplicates.
 */
export function reportAppCommandOutcome(
  command: AppCommandRef,
  outcome: AgentAppCommandOutcome,
  reason?: string,
) {
  outbox.set(`${command.app_command_id}:${outcome}`, {
    type: 'app_command_ack',
    request_id: crypto.randomUUID(),
    conversation_id: command.conversation_id,
    turn_id: command.turn_id,
    app_command_id: command.app_command_id,
    client_id: getAgentClientId(),
    outcome,
    reason: reason?.slice(0, 500) ?? null,
  });
  saveOutbox();
  sendAll();
}

/** Lets an agent session send acks; returns the detach function. */
export function attachAppCommandAckSender(send: Sender) {
  senders.add(send);
  return () => {
    senders.delete(send);
  };
}

/** Sends acks the server has not recorded yet, e.g. after reconnecting. */
export const flushAppCommandAcks = sendAll;

/** Drops an ack once the server recorded it, or refused it for good. */
export function settleAppCommandAck(result: AppCommandAckResult) {
  if (result.outcome === 'rejected' && result.retryable) return;
  for (const [key, ack] of outbox) {
    if (ack.request_id === result.request_id) outbox.delete(key);
  }
  saveOutbox();
}
